import { useState, type FormEvent } from 'react'
import type { ColumnType, Project } from '../api/types'
import { needsServer } from '../components/needsServer'
import { useMounted } from '../components/layout'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { useBoardState, useStore } from '../store/context'
import { COLUMN_LIMIT, COLUMN_NAME_MAX, COLUMN_TYPES, columnState, ownColumns, STATES } from '../vocab'
import { useSettingsProject } from './selection'
import { settingsWords } from './words'

const words = settingsWords.columns

/** A column of the list being edited: one not saved yet has no id. */
interface Draft { id?: string; name: string; type: ColumnType }
const typeName = (type: ColumnType) => COLUMN_TYPES.find(t => t.type === type)?.name ?? type

/**
 * A project's own board columns (Poulpe-441), as today's Settings has them: each with a
 * name and the type that says which state its issues are in, in the order the board
 * shows them. A column that holds issues is not removed before they move. One Save.
 */
export function ColumnSettings() {
  const project = useSettingsProject()
  return project ? <section className="settings-section"><Columns key={project.id} project={project} /></section> : null
}

function Columns({ project }: { project: Project }) {
  const store = useStore()
  const { issues, offline } = useBoardState()
  const mounted = useMounted()
  const saved = ownColumns(project)
  const shown = (list: readonly Draft[]) => JSON.stringify(list.map(c => [c.id ?? null, c.name.trim(), c.type]))
  const [columns, setColumns] = useState<Draft[]>(() => saved.map(c => ({ ...c })))
  const [name, setName] = useState('')
  const [type, setType] = useState<ColumnType>('unstarted')
  const [removing, setRemoving] = useState<{ index: number; count: number } | null>(null)
  const [target, setTarget] = useState('todo')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const dirty = shown(columns) !== shown(saved)
  useUnsavedChanges(dirty && !busy)
  const held = (id: string) => [...issues.values()].filter(i => i.project === project.id && i.column === id)

  function add() {
    const next = name.trim()
    if (!next || columns.length >= COLUMN_LIMIT) return
    setColumns([...columns, { name: next, type }])
    setName('')
    setProblem(null)
  }
  function rename(index: number, value: string) { setColumns(columns.map((c, at) => (at === index ? { ...c, name: value } : c))) }
  function shift(index: number, by: number) {
    const next = [...columns]
    ;[next[index], next[index + by]] = [next[index + by]!, next[index]!]
    setColumns(next)
  }
  function remove(index: number) {
    const column = columns[index]
    const count = column?.id ? held(column.id).length : 0
    if (count > 0) { setRemoving({ index, count }); setTarget('todo') }
    else { setColumns(columns.filter((_, at) => at !== index)); setRemoving(null) }
  }
  async function moveAndRemove() {
    const column = removing ? columns[removing.index] : undefined
    if (!removing || !column?.id || busy || offline) return
    setBusy(true); setProblem(null)
    try {
      const other = columns.find(c => c.id === target)
      for (const issue of held(column.id)) {
        const done = other ? await store.moveIssue(issue.id, columnState({ type: other.type }), undefined, other.id) : await store.moveIssue(issue.id, target as (typeof STATES)[number]['slug'])
        if (!done) throw new Error(words.failed)
      }
      if (!mounted.current) return
      setColumns(current => current.filter(c => c.id !== column.id))
      setRemoving(null)
    } catch (error) { if (mounted.current) setProblem(error instanceof Error ? error.message : words.failed) }
    finally { if (mounted.current) setBusy(false) }
  }
  async function save(event: FormEvent) {
    event.preventDefault()
    if (busy || offline || !dirty) return
    setBusy(true); setProblem(null)
    const outcome = await store.setProjectColumns(project.id, columns.map(c => ({ ...(c.id ? { id: c.id } : {}), name: c.name.trim(), type: c.type })))
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) toast(settingsWords.saved)
    else setProblem(outcome.message)
  }
  const targets = [
    ...STATES.filter(s => s.slug !== 'on_main').map(s => ({ value: s.slug as string, name: s.name })),
    ...columns.filter(c => c.id && c.id !== (removing && columns[removing.index]?.id)).map(c => ({ value: c.id!, name: c.name })),
  ]
  return <form className="setup-form" onSubmit={event => void save(event)}>
    <p className="muted">{words.about}</p>
    <ul className="settings-rows settings-columns">
      {columns.map((column, index) => <li key={column.id ?? `new-${index}`}><div className="settings-row-line">
        <input className="settings-row-what" aria-label={`${words.name}: ${column.name}`} value={column.name} maxLength={COLUMN_NAME_MAX} disabled={busy || offline} {...needsServer(offline)}
          onChange={event => rename(index, event.target.value)} />
        <span className="muted" title={words.typeAbout}>{typeName(column.type)}</span>
        <button type="button" className="button button-quiet" aria-label={`${words.up}: ${column.name}`} disabled={busy || index === 0} onClick={() => shift(index, -1)}>{words.up}</button>
        <button type="button" className="button button-quiet" aria-label={`${words.down}: ${column.name}`} disabled={busy || index === columns.length - 1} onClick={() => shift(index, 1)}>{words.down}</button>
        <button type="button" className="button button-quiet" aria-label={`${words.remove} ${column.name}`} disabled={busy} onClick={() => remove(index)}>{words.remove}</button>
      </div></li>)}
      {columns.length < COLUMN_LIMIT && <li><div className="settings-row-line">
        <input className="settings-row-what" aria-label={words.newName} placeholder={words.newColumn} value={name} maxLength={COLUMN_NAME_MAX} disabled={busy || offline} {...needsServer(offline)}
          onChange={event => setName(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); add() } }} />
        <label className="settings-column-after"><span>{words.type}</span>
          <select value={type} disabled={busy} onChange={event => setType(event.target.value as ColumnType)}>
            {COLUMN_TYPES.map(t => <option key={t.type} value={t.type}>{t.name}</option>)}
          </select>
        </label>
        <button type="button" className="button" disabled={busy || !name.trim()} onClick={add}>{words.add}</button>
      </div></li>}
    </ul>
    {removing && <div className="settings-accept-band">
      <p>{words.holds(removing.count, columns[removing.index]?.name ?? '')}</p>
      <label className="field"><span>{words.moveTo}</span>
        <select value={target} disabled={busy} onChange={event => setTarget(event.target.value)}>
          {targets.map(t => <option key={t.value} value={t.value}>{t.name}</option>)}
        </select>
      </label>
      <div className="form-actions">
        <button type="button" className="button" disabled={busy || offline} {...needsServer(offline)} onClick={() => void moveAndRemove()}>{words.move}</button>
        <button type="button" className="button" disabled={busy} onClick={() => setRemoving(null)}>{words.keep}</button>
      </div>
    </div>}
    {problem && <p className="text-danger" role="alert">{problem}</p>}
    <div className="form-actions">
      <button type="submit" className="button button-primary" disabled={busy || offline || !dirty || columns.some(c => !c.name.trim())} {...needsServer(offline)}>{busy ? words.saving : words.save}</button>
    </div>
  </form>
}
